import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, Box, Typography, Button, IconButton } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import RestartAltOutlined from '@mui/icons-material/RestartAltOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';

/** Tokens aligned with chaavi_filter_modal.html */
const F = {
  bg: '#ffffff',
  border: '#d6cfc0',
  rule: '#ede8dc',
  text: '#3a2e1e',
  muted: '#7a6a55',
  secLabel: '#a89880',
  accent: '#c0873a',
  accentHover: '#a87030',
  footBg: '#fdfbf7',
  pillBorder: '#d6cfc0',
  pillHover: '#8a5a1a',
  chipBg: '#fdf0e0',
  chipText: '#b07030',
  subtle: '#c0b8a8',
};

const BUDGET_MAX = 500_000;
const BUDGET_MIN = 5_000;
const BUDGET_STEP = 5_000;

export interface FilterOptions {
  eventType: string;
  category: string;
  location: 'all' | 'nearby' | 'custom';
  radius: number;
  budget: 'all' | 'custom';
  minBudget: number;
  maxBudget: number;
  sortBy:
    | 'default'
    | 'price-low'
    | 'price-high'
    | 'rating-high'
    | 'rating-low'
    | 'popular'
    | 'newest';
  minRating: number;
}

interface FilterDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterOptions) => void;
  onReset: () => void;
  initialFilters?: Partial<FilterOptions>;
}

const DEFAULT_FILTERS: FilterOptions = {
  eventType: 'all',
  category: 'all',
  location: 'all',
  radius: 10,
  budget: 'all',
  minBudget: 0,
  maxBudget: 100_000,
  sortBy: 'default',
  minRating: 0,
};

const EVENT_PILLS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'wedding', label: 'Wedding' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'mehendi', label: 'Mehendi' },
  { value: 'reception', label: 'Reception' },
  { value: 'birthday', label: 'Birthday' },
];

const CATEGORY_PILLS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'tent', label: 'Decor' },
  { value: 'caters', label: 'Catering' },
  { value: 'photography', label: 'Photography' },
  { value: 'music', label: 'Music' },
  { value: 'makeup', label: 'Makeup' },
  { value: 'transport', label: 'Transport' },
];

const LOCATION_OPTIONS: { value: string; label: string; km?: number }[] = [
  { value: 'all', label: 'All locations' },
  { value: '5', label: 'Within 5 km', km: 5 },
  { value: '10', label: 'Within 10 km', km: 10 },
  { value: '25', label: 'Within 25 km', km: 25 },
  { value: '50', label: 'Within 50 km', km: 50 },
];

const SORT_OPTIONS: { value: FilterOptions['sortBy']; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'price-low', label: 'Price: low to high' },
  { value: 'price-high', label: 'Price: high to low' },
  { value: 'rating-high', label: 'Top rated' },
  { value: 'popular', label: 'Most popular' },
  { value: 'newest', label: 'Newest' },
];

function deriveLocationSelect(f: FilterOptions): string {
  if (f.location === 'all') return 'all';
  const r = f.radius;
  if ([5, 10, 25, 50].includes(r)) return String(r);
  if (f.location === 'nearby') return '10';
  return String(r <= 50 ? r : 10);
}

function formatRupee(n: number): string {
  if (n >= BUDGET_MAX) return 'No limit';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(n);
}

function countNonDefault(filters: FilterOptions): number {
  let n = 0;
  if (filters.eventType !== 'all') n++;
  if (filters.category !== 'all') n++;
  if (filters.location !== 'all') n++;
  if (filters.budget === 'custom' && filters.maxBudget < BUDGET_MAX) n++;
  if (filters.sortBy !== 'default') n++;
  if (filters.minRating > 0) n++;
  return n;
}

const selectSx = {
  width: '100%',
  py: 1.1,
  px: 1.5,
  borderRadius: '8px',
  border: `0.5px solid ${F.border}`,
  bgcolor: '#fafaf7',
  color: F.text,
  fontSize: '0.8125rem',
  fontFamily: '"Jost", "Roboto", sans-serif',
  cursor: 'pointer',
  appearance: 'none' as const,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23a89880' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  pr: 3.5,
  '&:focus': {
    outline: 'none',
    borderColor: F.accent,
  },
  '&:hover': {
    borderColor: '#b0a090',
  },
};

const FilterDialog: React.FC<FilterDialogProps> = ({
  open,
  onClose,
  onApply,
  onReset,
  initialFilters,
}) => {
  const [filters, setFilters] = useState<FilterOptions>({ ...DEFAULT_FILTERS, ...initialFilters });
  const [locationSelect, setLocationSelect] = useState('all');
  const [budgetSlider, setBudgetSlider] = useState(BUDGET_MAX);

  useEffect(() => {
    if (open) {
      const merged = { ...DEFAULT_FILTERS, ...initialFilters };
      setFilters(merged);
      setLocationSelect(deriveLocationSelect(merged));
      setBudgetSlider(
        merged.budget === 'all' ? BUDGET_MAX : Math.min(BUDGET_MAX, Math.max(BUDGET_MIN, merged.maxBudget))
      );
    }
  }, [open, initialFilters]);

  const draftActiveCount = useMemo(() => {
    const loc = locationSelect === 'all' ? DEFAULT_FILTERS.location : 'custom';
    const rad =
      locationSelect === 'all' ? DEFAULT_FILTERS.radius : Number(locationSelect) || 10;
    const budg = budgetSlider >= BUDGET_MAX ? 'all' : 'custom';
    const maxB = budgetSlider >= BUDGET_MAX ? DEFAULT_FILTERS.maxBudget : budgetSlider;
    return countNonDefault({
      ...filters,
      location: loc,
      radius: rad,
      budget: budg,
      maxBudget: maxB,
    });
  }, [filters, locationSelect, budgetSlider]);

  const handlePill = (group: 'event' | 'cat', value: string) => {
    if (group === 'event') {
      setFilters(prev => ({ ...prev, eventType: value }));
    } else {
      setFilters(prev => ({ ...prev, category: value }));
    }
  };

  const handleApply = () => {
    const loc = locationSelect === 'all' ? 'all' : 'custom';
    const rad = locationSelect === 'all' ? 10 : Number(locationSelect) || 10;
    const budget = budgetSlider >= BUDGET_MAX ? 'all' : 'custom';
    const maxBudget = budgetSlider >= BUDGET_MAX ? DEFAULT_FILTERS.maxBudget : budgetSlider;

    const next: FilterOptions = {
      ...filters,
      location: loc,
      radius: rad,
      budget,
      minBudget: 0,
      maxBudget,
    };
    onApply(next);
    onClose();
  };

  const handleReset = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setLocationSelect('all');
    setBudgetSlider(BUDGET_MAX);
    onReset();
  };

  const pillSx = (active: boolean) => ({
    py: 0.85,
    px: 1.75,
    borderRadius: '20px',
    border: `0.5px solid ${active ? F.accent : F.pillBorder}`,
    bgcolor: active ? F.accent : '#fff',
    color: active ? '#fff' : '#5a4a35',
    fontSize: '0.8125rem',
    fontFamily: '"Jost", "Roboto", sans-serif',
    cursor: 'pointer',
    transition: 'all 0.12s',
    '&:hover': {
      borderColor: F.accent,
      color: active ? '#fff' : F.pillHover,
    },
  });

  const ratingBtnSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    py: 0.85,
    px: 1.5,
    borderRadius: '8px',
    border: `0.5px solid ${active ? F.accent : F.pillBorder}`,
    bgcolor: active ? F.chipBg : '#fff',
    color: active ? F.pillHover : '#5a4a35',
    fontSize: '0.8125rem',
    fontFamily: '"Jost", "Roboto", sans-serif',
    cursor: 'pointer',
    '&:hover': { borderColor: F.accent },
  });

  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography
      sx={{
        fontSize: '0.6875rem',
        fontWeight: 500,
        letterSpacing: '0.08em',
        color: F.secLabel,
        textTransform: 'uppercase',
        fontFamily: '"Jost", sans-serif',
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          border: `0.5px solid ${F.border}`,
          maxWidth: 680,
          maxHeight: 'min(90dvh, calc(100vh - 32px))',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: F.bg,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: `0.5px solid ${F.rule}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <TuneOutlined sx={{ fontSize: 20, color: F.accent }} />
          <Typography
            sx={{
              fontSize: '1.0625rem',
              fontWeight: 500,
              color: F.text,
              fontFamily: '"Playfair Display", Georgia, serif',
            }}
          >
            Filters
          </Typography>
          {draftActiveCount > 0 && (
            <Typography
              component="span"
              sx={{
                bgcolor: F.chipBg,
                color: F.chipText,
                fontSize: '0.6875rem',
                fontWeight: 500,
                px: 1.1,
                py: 0.35,
                borderRadius: '20px',
                fontFamily: '"Jost", sans-serif',
              }}
            >
              {draftActiveCount} active
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: F.muted,
            '&:hover': { bgcolor: '#f0ebe0' },
          }}
          aria-label="Close"
        >
          <CloseRounded fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 3,
          py: 2.5,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box sx={{ py: 2, borderBottom: `0.5px solid #f0ebe0` }}>
          <SectionLabel>Event type</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {EVENT_PILLS.map(p => (
              <button key={p.value} type="button" style={{ border: 'none', margin: 0 }} onClick={() => handlePill('event', p.value)}>
                <Box component="span" sx={pillSx(filters.eventType === p.value)}>
                  {p.label}
                </Box>
              </button>
            ))}
          </Box>
        </Box>

        <Box sx={{ py: 2, borderBottom: `0.5px solid #f0ebe0` }}>
          <SectionLabel>Category</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {CATEGORY_PILLS.map(p => (
              <button key={p.value} type="button" style={{ border: 'none', margin: 0 }} onClick={() => handlePill('cat', p.value)}>
                <Box component="span" sx={pillSx(filters.category === p.value)}>
                  {p.label}
                </Box>
              </button>
            ))}
          </Box>
        </Box>

        <Box sx={{ py: 2, borderBottom: `0.5px solid #f0ebe0` }}>
          <SectionLabel>Location &amp; sort</SectionLabel>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography sx={{ fontSize: '0.75rem', color: F.muted, fontFamily: '"Jost", sans-serif' }}>
                Location range
              </Typography>
              <Box
                component="select"
                value={locationSelect}
                onChange={e => setLocationSelect(e.target.value)}
                sx={selectSx}
              >
                {LOCATION_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography sx={{ fontSize: '0.75rem', color: F.muted, fontFamily: '"Jost", sans-serif' }}>
                Sort by
              </Typography>
              <Box
                component="select"
                value={filters.sortBy}
                onChange={e => setFilters(prev => ({ ...prev, sortBy: e.target.value as FilterOptions['sortBy'] }))}
                sx={selectSx}
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ py: 2, borderBottom: `0.5px solid #f0ebe0` }}>
          <SectionLabel>Budget range</SectionLabel>
          <Box sx={{ pt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.75rem', color: F.muted, fontFamily: '"Jost", sans-serif' }}>Up to</Typography>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: F.accent, fontFamily: '"Jost", sans-serif' }}>
                {formatRupee(budgetSlider)}
              </Typography>
            </Box>
            <input
              type="range"
              min={BUDGET_MIN}
              max={BUDGET_MAX}
              step={BUDGET_STEP}
              value={budgetSlider}
              onChange={e => setBudgetSlider(Number(e.target.value))}
              style={{
                width: '100%',
                accentColor: F.accent,
                height: 4,
                cursor: 'pointer',
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
              <Typography sx={{ fontSize: '0.6875rem', color: F.subtle, fontFamily: '"Jost", sans-serif' }}>₹5,000</Typography>
              <Typography sx={{ fontSize: '0.6875rem', color: F.subtle, fontFamily: '"Jost", sans-serif' }}>No limit</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ py: 2 }}>
          <SectionLabel>Minimum rating</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[
              { v: 0, label: 'Any' as const, icon: false },
              { v: 3, label: '3+' as const, icon: true },
              { v: 4, label: '4+' as const, icon: true },
              { v: 4.5, label: '4.5+' as const, icon: true },
            ].map(row => (
              <button
                key={row.v}
                type="button"
                onClick={() => setFilters(prev => ({ ...prev, minRating: row.v }))}
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
              >
                <Box sx={ratingBtnSx(filters.minRating === row.v)}>
                  {row.icon && (
                    <Box
                      component="svg"
                      viewBox="0 0 16 16"
                      sx={{ width: 14, height: 14, fill: F.accent }}
                    >
                      <path d="M8 1l1.8 3.6L14 5.4l-3 2.9.7 4.1L8 10.4l-3.7 2 .7-4.1-3-2.9 4.2-.8z" />
                    </Box>
                  )}
                  {row.label}
                </Box>
              </button>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderTop: `0.5px solid ${F.rule}`,
          bgcolor: F.footBg,
          flexWrap: 'wrap',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Button
          type="button"
          onClick={handleReset}
          startIcon={<RestartAltOutlined sx={{ fontSize: 18, color: F.secLabel }} />}
          sx={{
            textTransform: 'none',
            color: F.secLabel,
            fontFamily: '"Jost", sans-serif',
            fontSize: '0.8125rem',
            '&:hover': { color: F.muted, bgcolor: 'transparent' },
          }}
        >
          Reset all
        </Button>
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              borderColor: F.border,
              color: '#5a4a35',
              fontFamily: '"Jost", sans-serif',
              fontSize: '0.8125rem',
              py: 1,
              px: 2.25,
              '&:hover': { borderColor: F.border, bgcolor: '#f5f0e8' },
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            disableElevation
            onClick={handleApply}
            startIcon={<CheckOutlined sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              bgcolor: F.accent,
              fontFamily: '"Jost", sans-serif',
              fontSize: '0.8125rem',
              fontWeight: 500,
              py: 1,
              px: 2.5,
              '&:hover': { bgcolor: F.accentHover },
            }}
          >
            Apply filters
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FilterDialog;
